import { useState, useEffect, ReactElement } from 'react'
import { MapDispatchToProps, MapStateToProps, connect } from 'react-redux'
import { useQuery, useMutation, queryCache } from 'react-query'
import * as api from './evaluationForm.api'
import {
  templateSelector,
  sanitizeValues,
  indexComments,
  getChartData,
  getDefaultAnswers,
  hasValidAnswers,
  getAlphabetizedActiveForms,
  isEvaluationLoaded,
} from './evaluationForm.selectors'
import { getActiveEvaluationForms } from './evaluationForm.actions'
import { updateContactAfterEvaluationSave, updateContactAfterEvaluationReset } from '../MediaPlayer/mediaPlayer.actions'
import { updateRecordingsContactsAfterEvaluationReset } from '../recordings.actions'
import EvaluationPanel from './EvaluationPanel'
import { difference } from '../../../Common/Utils'
import { addCommentSuccess, updateCommentSuccess } from '../AudioPanel/comments.actions'
import { ACTIONS as GOAL_ACTIONS } from '../CreateTask/createTask.constants'
import { RootState, useAppDispatch } from '../../../root.reducer'
import { IndexedChatBubble } from '../ChatMapPanel/constants'

interface EvaluationOwnProps {
  contactId: number,
  evalId: number,
  evalFormId: number,
  goal: $TSFIXME,
  agentId: number,
  agentDisplayName: string,
  contactDateTime: $TSFIXME,
  callDuration: number,
  qualityEvaluationAllowed: boolean,
  calibrationEvaluationAllowed: boolean,
  isDocked: boolean,
  setDocked: $TSFIXME,
  arePopupsEnabled: boolean,
  showManualEvalForm: boolean,
  showAiEvalForm: boolean,
  autoQAHasData: boolean,
  autoQAFormId: number,
  onPopupError: (...args: $TSFIXME[]) => $TSFIXME,
  onPopupsBlocked: (...args: $TSFIXME[]) => $TSFIXME,
  onGetMediaCurrentTime: (...args: $TSFIXME[]) => $TSFIXME,
  indexedChatBubbles: IndexedChatBubble[],
}

/** @deprecated useAppSelector instead */
interface EvaluationStateProps {
  /** @deprecated useAppSelector instead */
  activeForms: $TSFIXME[],
}

/** @deprecated useAppDispatch instead */
interface EvaluationDispatchProps {
  /** @deprecated useAppDispatch instead */
  onSaveEval: (evaluation: api.Eval, contactId: number) => void,
  /** @deprecated useAppDispatch instead */
  onResetEval: (contactId: number) => void,
}

/** @deprecated useAppSelector instead */
const mapStateToProps: MapStateToProps<EvaluationStateProps, EvaluationOwnProps, RootState> = ({ evaluation }) => ({
  activeForms: getAlphabetizedActiveForms(evaluation),
})

/** @deprecated useAppDispatch instead */
const mapDispatchToProps: MapDispatchToProps<EvaluationDispatchProps, EvaluationOwnProps> = dispatch => {
  const onSaveEval = async (evaluation: api.Eval, contactId: number) => {
    dispatch(updateContactAfterEvaluationSave(evaluation, contactId))
    // dispatch(updateRecordingsContactsAfterEvaluationSave(response, ccrId)) // TODO: enable once contact grid renders efficiently
  }

  const onResetEval = async (contactId: number) => {
    dispatch(updateContactAfterEvaluationReset())
    dispatch(updateRecordingsContactsAfterEvaluationReset(contactId))
  }

  return {
    onSaveEval,
    onResetEval,
  }
}

interface EvaluationProps extends EvaluationOwnProps, EvaluationStateProps, EvaluationDispatchProps {}

function SingleEvaluation({
  contactId,
  evalId,
  evalFormId,
  activeForms,
  agentId,
  callDuration,
  goal,
  arePopupsEnabled,
  isDocked,
  qualityEvaluationAllowed,
  calibrationEvaluationAllowed,
  onPopupsBlocked,
  onPopupError,
  setDocked,
  onGetMediaCurrentTime,
  onSaveEval,
  onResetEval,
  agentDisplayName,
  contactDateTime,
  showManualEvalForm,
  showAiEvalForm,
  autoQAHasData,
  autoQAFormId,
  indexedChatBubbles,
}: EvaluationProps): ReactElement {
  const dispatch = useAppDispatch()
  const [isCalibrationView, setIsCalibrationView] = useState(Boolean((!qualityEvaluationAllowed && calibrationEvaluationAllowed) || (calibrationEvaluationAllowed && goal && goal.action === GOAL_ACTIONS.CALIBRATE)))
  const [templateId, setTemplateId] = useState(!isCalibrationView ? evalFormId : null)
  const [sectionsBeingUpdated, setSectionsBeingUpdated] = useState<number[]>([])

  useEffect(() => {
    if (qualityEvaluationAllowed) {
      dispatch(getActiveEvaluationForms())
    }
  }, [])

  // GET calibration details if enabled
  const calibrationDetails = useQuery(
    ['calibrationDetails', { contactId }],
    api.getCalibration,
    {
      enabled: calibrationEvaluationAllowed,
      refetchOnWindowFocus: false,
    },
  )

  // Once calibration details request is successfull, update template id for calibration form
  useEffect(() => {
    if (calibrationDetails.isSuccess && isCalibrationView) {
      // @ts-expect-error TODO: determine whether parseInt is necessary
      setTemplateId(parseInt(calibrationDetails.data.calibrationForm, 10))
    }
  }, [calibrationDetails.isSuccess])

  // GET form template if enabled
  const template = useQuery(
    ['template', { templateId }],
    api.getTemplate,
    { enabled: templateId, refetchOnWindowFocus: false },
  )
  // GET values if enabled
  const values = useQuery(
    ['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }],
    isCalibrationView ? api.getCalibrationValues : api.getEvaluation,
    {
      enabled: contactId && (isCalibrationView || evalId),
      refetchOnWindowFocus: false,
      onError: () => {
        queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], null)
      },
    },
  )

  const evaluationId = values.isSuccess
    ? values.data
      ? values.data.id
      : null
    : evalId

  // GET comments if enabled
  const comments = useQuery(
    ['comments', { contactId, evalId: evaluationId }],
    api.getComments,
    { enabled: contactId && evaluationId, refetchOnWindowFocus: false },
  )

  const [saveValues, { isLoading: isSavingValues }] = useMutation(api.saveEvaluation, {
    // When mutate is called:
    onMutate: ({ contactId, evalId, newValues, isCalibrationView }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      queryCache.cancelQueries(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], { exact: true })

      // Snapshot the previous value
      const previousValues = queryCache.getQueryData<api.Eval>(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }]) || null

      // Optimistically update to the new value
      queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], newValues)

      if (previousValues) {
        // Determine and set a list of section ids for sections that have changes being saved
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: use a type predicate to narrow the type of diff
        const diff = difference(newValues, previousValues) as api.EvaluationFormValues
        if (diff?.sections) {
          const updatedOptions = diff.sections.reduce<$TSFIXME[]>((acc, { questions }) => {
            questions.forEach(({ selectedOption }) => acc.push(selectedOption))
            return acc
          }, [])
          const updatedSections = newValues.sections.reduce<number[]>((acc, { id, questions }) => {
            if (questions.some(({ selectedOption }) => updatedOptions.includes(selectedOption))) {
              acc.push(id)
            }
            return acc
          }, [])
          setSectionsBeingUpdated(updatedSections)
        }
      }

      // Return the snapshotted value
      return () => queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], previousValues)
    },
    onSuccess: (data, { contactId, evalId, isCalibrationView }) => {
      const optimisticData = queryCache.getQueryData<api.Eval>(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }])
      // use new score information but don't clobber any new optimistically selected options
      const newData = {
        ...data,
        sections: optimisticData ? data.sections.map((section, sectionIndex) => {
          return {
            ...section,
            questions: section.questions.map((question, questionIndex) => {
              return {
                ...question,
                // @ts-expect-error TODO: type-check indexed access
                selectedOption: optimisticData.sections[sectionIndex].questions[questionIndex].selectedOption,
              }
            }),
          }
        }) : data.sections,
      }

      if (evalId === null) { // create old cache with null evalId set to null
        queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], null)
      }
      queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : data.id, isCalibrationView }], newData)

      if (!isCalibrationView) {
        onSaveEval(data, contactId)
      }
    },
    onSettled: () => {
      setSectionsBeingUpdated([])
    },
  })

  const [resetEvaluation] = useMutation(api.resetEvaluation, {
    // When mutate is called:
    onMutate: ({ contactId, evalId, isCalibrationView }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      queryCache.cancelQueries(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }])

      // Snapshot the previous values and comments
      const previousValues = queryCache.getQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }])
      const previousComments = queryCache.getQueryData(['comments', { contactId, evalId }])

      // Optimistically update to the new value
      queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], null)
      queryCache.setQueryData(['comments', { contactId, evalId }], [])

      // Return the snapshotted value
      return () => {
        queryCache.setQueryData(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], previousValues)
        queryCache.setQueryData(['comments', { contactId, evalId }], previousComments)
      }
    },
    onSuccess: (data, { contactId, evalId, isCalibrationView }) => {
      queryCache.removeQueries(['values', { contactId, evalId: isCalibrationView ? 'calibration' : evalId, isCalibrationView }], { exact: true })
      queryCache.removeQueries(['comments', { contactId, evalId }], { exact: true })
      if (!isCalibrationView) {
        onResetEval(contactId)
      }
    },
    // If the mutation fails, use the value returned from onMutate to roll back
    onError: (err, newValues, rollback) => rollback(),
  })

  const [addComment] = useMutation(api.addComment, {
    onSuccess: async (data, { isCalibrationView }) => {
      await comments.refetch()
      if (!isCalibrationView && data !== null) {
        dispatch(addCommentSuccess(data))
      }
    },
  })

  const [updateComment] = useMutation(api.updateComment, {
    onSuccess: async data => {
      await comments.refetch()
      if (!isCalibrationView && data !== null) {
        dispatch(updateCommentSuccess(data))
      }
    },
  })

  const evaluation: api.EvaluationData = {
    template: {
      ...template,
      data: typeof template.data === 'undefined' ? null : template.data,
    },
    values: {
      ...values,
      data: typeof values.data === 'undefined' ? null : values.data,
    },
    comments: {
      ...comments,
      data: typeof comments.data === 'undefined' ? [] : comments.data,
    },
  }

  const handleSetEvaluationView = () => {
    setIsCalibrationView(false)
    setTemplateId(evalFormId)
  }

  const handleSetCalibrationView = () => {
    setIsCalibrationView(true)
    // @ts-expect-error TODO: handle undefined calibrationDetails.data
    setTemplateId(calibrationDetails.data.calibrationForm)
  }

  const handleGetEvalForm = (templateId: number) => setTemplateId(templateId)

  const handleGetEvalComments = () => queryCache.invalidateQueries(['comments', { contactId, evaluationId }])

  const handleSaveEval = async (contactId: number, evalId: number, newValues: api.EvaluationFormValues) => {
    await saveValues({ contactId, evalId, newValues, isCalibrationView })
  }

  const handleAddComment = (contactId: number, evalId: number, comment: api.DraftComment) => {
    addComment({ contactId, evalId, comment, isCalibrationView })
  }

  const handleUpdateComment = (comment: api.DraftComment, commentRef: string): void => {
    updateComment({ comment, commentRef })
  }

  const handleResetEval = (contactId: number, evalId: number) => {
    resetEvaluation({ contactId, evalId, isCalibrationView })
  }

  const sanitizedValues = sanitizeValues(evaluation)
  if (sanitizedValues && typeof sanitizedValues.agent === 'undefined') {
    // @ts-expect-error TODO: fix error
    sanitizedValues.agent = { id: agentId }
  }

  return (
    <EvaluationPanel
      contactId={contactId}
      agentDisplayName={agentDisplayName}
      contactDateTime={contactDateTime}
      activeForms={activeForms}
      callDuration={callDuration}
      arePopupsEnabled={arePopupsEnabled}
      isDocked={isDocked}
      qualityEvaluationAllowed={qualityEvaluationAllowed}
      calibrationEvaluationAllowed={calibrationEvaluationAllowed}
      onPopupsBlocked={onPopupsBlocked}
      onPopupError={onPopupError}
      setDocked={setDocked}
      onGetMediaCurrentTime={onGetMediaCurrentTime}
      evalId={evaluation.values.data ? evaluation.values.data.id : null}
      template={templateSelector(evaluation)}
      values={sanitizedValues}
      comments={indexComments(evaluation)}
      // @ts-expect-error TODO: fix error
      chartData={getChartData(evaluation)}
      defaultAnswers={getDefaultAnswers(evaluation)}
      selectedForm={evaluation.template.data}
      sectionsBeingUpdated={sectionsBeingUpdated}
      hasValidAnswers={hasValidAnswers(evaluation)}
      isValuesLoading={values.isLoading}
      isSavingValues={isSavingValues}
      isCalibrationView={isCalibrationView}
      evaluationLoaded={evalId === null || isEvaluationLoaded(evaluation)}
      onGetEvalForm={handleGetEvalForm}
      onGetEvalComments={handleGetEvalComments}
      onSaveEval={handleSaveEval}
      onResetEval={handleResetEval}
      onAddComment={handleAddComment}
      onUpdateComment={handleUpdateComment}
      onSetEvaluationView={handleSetEvaluationView}
      onSetCalibrationView={handleSetCalibrationView}
      showManualEvalForm={showManualEvalForm}
      showAiEvalForm={showAiEvalForm}
      autoQAHasData={autoQAHasData}
      autoQAFormId={autoQAFormId}
      indexedChatBubbles={indexedChatBubbles}
    />
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(SingleEvaluation)
